import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, fmtPct, fmtUsd } from "@/lib/api";
import { Search, Loader2, Sparkles, Radio } from "lucide-react";

import { useNavigate } from "react-router-dom";
import { UpgradeButton } from "@/components/Locked";
import { useBilling } from "@/context/BillingContext";

const ageInfo = (iso) => {
  if (!iso) return { text: "cached", delayed: false };
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m > 15) return { text: `delayed · updated ${m} min ago`, delayed: true };
  return { text: m < 1 ? "updated just now" : `updated ${m} min ago`, delayed: false };
};
const OPENED_KEY = "apx-search-opened";

export const SearchDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quotaMsg, setQuotaMsg] = useState("");
  const navigate = useNavigate();
  const { billing, plan } = useBilling();
  const searchQuota = plan === "free" ? billing?.usage?.coin_searches : null;
  const exhausted = searchQuota?.limit != null && searchQuota.used >= searchQuota.limit;
  const quotaNotice = quotaMsg || (exhausted ? "Daily coin search limit reached." : "");

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); setQuotaMsg(""); return; }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/market/search", { params: { q } });
        setResults((r.data.coins || []).slice(0, 12));
      } catch (e) {
        if (e?.response?.status === 402) setQuotaMsg(e.response.data?.detail?.message || "Daily search limit reached — upgrade for unlimited searches.");
        else console.warn("[apex] ignored:", e);
      }
      setLoading(false);
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = async (c) => {
    // Quota is charged once per coin opened from search (per day); re-opening the same coin this session is free.
    const opened = new Set(JSON.parse(sessionStorage.getItem(OPENED_KEY) || "[]"));
    if (!opened.has(c.id)) {
      try {
        const r = await api.post("/market/search/select", { coin_id: c.id });
        opened.add(c.id); sessionStorage.setItem(OPENED_KEY, JSON.stringify([...opened]));
        if (r.data?.charged) window.dispatchEvent(new Event("apx:billing-changed"));
      } catch (e) {
        if (e?.response?.status === 402) { setQuotaMsg(e.response.data?.detail?.message || "Daily coin search limit reached."); return; }
      }
    }
    onOpenChange(false);
    window.dispatchEvent(new Event("apx:pause-tour"));
    navigate(`/coin/${c.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="search-dialog"
        className="p-0 overflow-hidden max-w-xl border-slate-800 bg-slate-950/95"
      >
        <DialogTitle className="sr-only">Search cryptocurrencies</DialogTitle>
        <div className="flex items-center gap-3 px-4 py-3 pr-12 border-b border-slate-800">
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-cyan-400" /> : <Search className="w-4 h-4 text-slate-400" />}
          <input
            autoFocus
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search any cryptocurrency (BTC, Solana, Pepe…)"
            className="w-full bg-transparent outline-none text-sm placeholder:text-slate-500"
          />
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {searchQuota?.limit != null && !quotaNotice && (
            <div className="px-4 py-1.5 text-[10px] text-slate-500 font-mono-ticker border-b border-slate-900" data-testid="search-quota-remaining">
              Free plan · {Math.max(0, searchQuota.limit - searchQuota.used)} of {searchQuota.limit} coin lookups left today · typing is free, one is counted when you open a coin
            </div>
          )}
          {results.length > 0 && (
            <div className="px-4 py-1.5 text-[10px] text-amber-200/80 font-mono-ticker border-b border-slate-900" data-testid="search-credit-disclosure">
              Opening an AI Predictions coin without an analysis from the last 30 minutes generates a fresh one and uses 1 AI analysis credit. Price-only coins never do — they analyse only when you press Analyze.
            </div>
          )}
          {quotaNotice && (
            <div className="px-4 py-3 text-xs text-amber-300 border-b border-slate-900 flex items-center gap-3" data-testid="search-quota-msg">
              <span className="flex-1">{quotaNotice.replace(/\.?\s*$/, ".")} Pro and Premium include unlimited coin searches.</span>
              <UpgradeButton plan="pro" testid="search-quota-upgrade" label="Upgrade" />
            </div>
          )}
          {results.length === 0 && q && !loading && !quotaNotice && (
            <div className="p-8 text-center text-sm text-slate-500">No matches</div>
          )}
          {results.length === 0 && !q && (
            <div className="p-8 text-center text-xs text-slate-500 font-mono-ticker uppercase tracking-widest">
              Type to search across all coins
            </div>
          )}
          {results.map((c) => (
            <button
              key={c.id}
              data-testid={`search-result-${c.id}`}
              onClick={() => go(c)}
              className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 hover:bg-slate-800/50 text-left border-b border-slate-900"
            >
              <span className="text-xs text-slate-500 w-7 shrink-0 font-mono-ticker">{c.market_cap_rank ? `#${c.market_cap_rank}` : "—"}</span>
              {c.image || c.thumb ? <img src={c.image || c.thumb} alt="" className="w-6 h-6 rounded-full shrink-0" /> : <div className="w-6 h-6 rounded-full bg-slate-800 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{c.name} <span className="text-xs text-slate-500 uppercase font-mono-ticker">{c.symbol}</span></div>
                <div className="text-[10px] mt-0.5">
                  {c.ai_predictions
                    ? <span className="inline-flex items-center gap-1 text-cyan-300" data-testid={`badge-ai-${c.id}`}><Sparkles className="w-3 h-3" /> AI Predictions</span>
                    : <span className="text-slate-500" data-testid={`badge-price-only-${c.id}`}>Price Data Only</span>}
                  {c.live_venue
                    ? <span className="ml-2 inline-flex items-center gap-1 text-emerald-300" data-testid={`badge-live-${c.id}`}><Radio className="w-3 h-3" /> Live price feed</span>
                    : <span className="ml-2 text-slate-600" data-testid={`badge-nolive-${c.id}`}>no live pair · polled</span>}
                </div>
              </div>
              <div className="text-right shrink-0 font-mono-ticker" data-testid={`search-price-${c.id}`}>
                {c.price != null
                  ? <><div className="text-sm text-slate-100">{fmtUsd(c.price)}</div><div className={`text-xs ${c.change_24h == null ? "text-slate-500" : c.change_24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>{c.change_24h == null ? "—" : fmtPct(c.change_24h)}</div>{(() => { const a = ageInfo(c.price_updated_at); return <div className={`text-[9px] ${a.delayed ? "text-amber-400" : "text-slate-500"}`} data-testid={`search-price-age-${c.id}`}>{a.text}</div>; })()}</>
                  : <div className="text-[11px] text-cyan-300/80" data-testid={`search-view-price-${c.id}`}>View price →</div>}
              </div>
            </button>
          ))}
          {results.length > 0 && (
            <div className="px-4 py-2 text-[10px] text-slate-500 font-mono-ticker" data-testid="search-footnote">
              Results ranked by market cap · snapshot prices show their last update time · one search is counted when you open a coin · AI Predictions = coins in the daily EV-IDEX prediction universe
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
